import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import RichEditor from "./RichEditior";
import FAQSection from "./FAQSection";
import TitleAndClientSection from "./TitleAndClientSection";
import ProjectSection from "./ProjectSection";
import AuthorSection from "./AuthorSection";
import UploadProjectFile from "./UploadProjectFile";
import ButtonSection from "./ButtonSection";
import { DateTime } from "luxon";
import apiRequestHelper from "../../../../utils/apiRequestHelper";

interface Heading {
  title: string;
  paragraphs: string[];
}

interface FAQ {
  question: string;
  answer: string;
}

interface Blog {
  title: string;
  image: File | null;
  pictureUrl?: string; // 👈 for preview
  slug: string;
  authorId: number;
  scheduledDate?: string;
  content_json: any; // tiptap JSON
  content_html: string;
  faqs: FAQ[];
}
function tiptapJsonHasText(doc: any): boolean {
  if (!doc || doc.type !== "doc" || !Array.isArray(doc.content)) return false;

  const nodeToText = (node: any): string => {
    if (!node) return "";
    if (node.type === "text") return node.text || "";
    if (Array.isArray(node.content))
      return node.content.map(nodeToText).join("");
    return "";
  };

  const text = doc.content.map(nodeToText).join("").trim();
  return text.length > 0; // adjust if you want a min length
}

const BlogSchema = Yup.object().shape({
  title: Yup.string().required("Title is required").min(5),
  image: Yup.mixed()
    .nullable()
    .test("fileType", "Unsupported file format", function (value) {
      const { pictureUrl } = this.parent;

      // If no new file and there’s already an existing image, allow it
      if (!value && pictureUrl) return true;

      // If no file at all and no existing image, fail
      if (!value) return this.createError({ message: "Image is required" });

      const file = value as File;
      return ["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(
        file.type
      );
    })
    .test("fileSize", "File size must not exceed 10MB", function (value) {
      const { pictureUrl } = this.parent;

      // If no new file and there’s already an existing image, allow it
      if (!value && pictureUrl) return true;

      // If no file at all and no existing image, fail
      if (!value) return this.createError({ message: "Image is required" });

      const file = value as File;
      return file.size <= 10 * 1024 * 1024; // 10 MB
    }),

  slug: Yup.string()
    .required("Slug is required")
    .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug must be URL-friendly"),
  authorId: Yup.number().required("Author is required").integer(),
  scheduledDate: Yup.string()
    .nullable()
    .optional()
    .test(
      "is-valid-iso",
      "Scheduled date must be in ISO format (yyyy-mm-ddTHH:mm)",
      (value) => {
        if (!value) return true;
        return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value); // basic ISO datetime check
      }
    ),
  content_json: Yup.mixed()
    .required("Content is required")
    .test("has-text", "Please add some content.", (val) =>
      tiptapJsonHasText(val)
    ),

  // keep html but we'll keep it in sync from the editor
  content_html: Yup.string().optional(),

  // FAQs validation
  faqs: Yup.array()
    .of(
      Yup.object().shape({
        question: Yup.string()
          .required("Question is required")
          .min(5, "Question must be at least 5 characters"),
        answer: Yup.string()
          .required("Answer is required")
          .min(10, "Answer must be at least 10 characters"),
      })
    )
    .optional(),
});

const CreateNewProjectForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [initialValues, setInitialValues] = useState<Blog | null>(null);

  useEffect(() => {
    if (!id) {
      toast.error("Missing blog ID");
      navigate("/blogs/cards");
      return;
    }

    apiRequestHelper
      .get(`/blogs/${id}`)
      .then((res) => {
        const blog = res.data.data.blog;
        setInitialValues({
          title: blog.title,
          slug: blog.slug,
          authorId: blog.authorId,
          scheduledDate: blog.scheduledDate
            ? DateTime.fromISO(blog.scheduledDate) // This is in UTC from DB
                .setZone("Europe/London") // Convert to your display timezone
                // .setZone("Asia/Karachi")
                .toFormat("yyyy-MM-dd'T'HH:mm") // Keep both date and time for datetime-local input
            : "",

          image: null, // form state (File)
          pictureUrl: blog.pictureUrl, // for preview

          // ✅ Prefill editor values from API (fallbacks included)
          content_json: blog.content_json ??
            blog.contentJson ??
            blog.bodyJson ?? { type: "doc", content: [{ type: "paragraph" }] },
          content_html:
            blog.content_html ?? blog.contentHtml ?? blog.bodyHtml ?? "",
          
          // ✅ Prefill FAQs if available
          faqs: blog.faqs ?? [],
        });
      })
      .catch(() => {
        toast.error("Failed to fetch blog for editing.");
        navigate("/blogs/cards");
      });
  }, [id]);

  const handleUpdate = async (values: Blog) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("slug", values.slug);
    formData.append("authorId", values.authorId.toString());
    if (values.image instanceof File) {
      formData.append("image", values.image);
    }

    if (values.scheduledDate) {
      formData.append("scheduledDate", values.scheduledDate);
    }

    // ✅ Include editor outputs
    formData.append("content_json", JSON.stringify(values.content_json));
    formData.append("content_html", values.content_html ?? "");

    // ✅ Include FAQs if present
    if (values.faqs && values.faqs.length > 0) {
      formData.append("faqs", JSON.stringify(values.faqs));
    }

    try {
      const res = await apiRequestHelper.put(`/blogs/${id}`, formData, true);
      toast.success("Blog updated successfully!");
      navigate("/blogs/cards");
    } catch (error: any) {
      console.error("❌ API Error:", error.response?.data || error);
      toast.error(
        error.response?.data?.messages?.[0]?.message || "Blog update failed."
      );
    }
  };

  if (!initialValues) return <p>Loading blog data...</p>;

  return (
    <Formik<Blog>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={BlogSchema}
      onSubmit={handleUpdate}
    >
      {({ isSubmitting, values }) => {
        return (
          <Form className="theme-form">
            <TitleAndClientSection />
            {/* <ProjectSection /> */}
            <AuthorSection />
            <RichEditor />
            <FAQSection />
            <UploadProjectFile />
            <ButtonSection />
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateNewProjectForm;
